import { useMemo } from "react";

type TimeInput24Props = {
  id?: string;
  name?: string;
  value: string;
  onChange: (value: string) => void;
  isDe?: boolean;
  className?: string;
  ariaLabel?: string;
};

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

/**
 * Fixed 24-hour time picker (HH:MM).
 * Uses two native selects so the display never falls back to the browser/OS
 * AM/PM 12-hour format, which happens with <input type="time">.
 */
export default function TimeInput24({
  id,
  name,
  value,
  onChange,
  isDe = true,
  className = "",
  ariaLabel,
}: TimeInput24Props) {
  const [hour, minute] = useMemo(() => {
    const match = /^(\d{1,2}):(\d{2})/.exec(value || "");
    if (!match) return ["", ""];
    return [match[1].padStart(2, "0"), match[2]];
  }, [value]);

  const update = (nextHour: string, nextMinute: string) => {
    if (nextHour === "" && nextMinute === "") {
      onChange("");
      return;
    }
    const hh = nextHour === "" ? "00" : nextHour;
    const mm = nextMinute === "" ? "00" : nextMinute;
    onChange(`${hh}:${mm}`);
  };

  const selectClass =
    "flex-1 min-w-0 bg-transparent text-sm text-foreground-950 text-center focus:outline-none cursor-pointer";

  return (
    <div id={id} className={`${className} flex items-center gap-1.5`}>
      {name && <input type="hidden" name={name} value={value} />}
      <span className="shrink-0 w-5 h-5 flex items-center justify-center text-foreground-400">
        <i className="ri-time-line"></i>
      </span>
      <select
        aria-label={ariaLabel ? `${ariaLabel} – ${isDe ? "Stunde" : "hour"}` : isDe ? "Stunde" : "hour"}
        value={hour}
        onChange={(e) => update(e.target.value, minute)}
        className={selectClass}
      >
        <option value="">--</option>
        {HOURS.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>
      <span className="shrink-0 text-sm text-foreground-500">:</span>
      <select
        aria-label={ariaLabel ? `${ariaLabel} – ${isDe ? "Minute" : "minute"}` : isDe ? "Minute" : "minute"}
        value={minute}
        onChange={(e) => update(hour, e.target.value)}
        className={selectClass}
      >
        <option value="">--</option>
        {MINUTES.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
    </div>
  );
}